import { mnemonicToSeedSync, validateMnemonic } from '@scure/bip39';
import { wordlist as englishWordlist } from '@scure/bip39/wordlists/english.js';
import { HDKey } from '@scure/bip32';
import { buildTx, signAndFinalize, skToAddr, mainnetPrefix, testnetPrefix, type UTXO as MayaUTXO } from '@mayaprotocol/zcash-js';
import { Buffer } from 'buffer';
import type { AxiosError } from 'axios';
import { walletScannerApi } from './api';

// Ensure Buffer exists in browser environments for dependencies that expect Node globals.
if (typeof globalThis !== 'undefined' && !(globalThis as any).Buffer) {
	(globalThis as any).Buffer = Buffer;
}

const SATOSHIS_PER_ZEC = 100000000;
const DEFAULT_ACCOUNT_INDEX = 0;
const DEFAULT_ADDRESS_INDEX = 0;
const MAX_ALLOWED_INDEX = 0x7fffffff;
const COIN_TYPE = 133; // Zcash BIP-44 coin type

type NetworkSetting = 'mainnet' | 'testnet';

export interface ManualAutoSendParams {
	mnemonic: string;
	recipient: string;
	amountZec: number;
	memo?: string;
	accountIndex?: number;
	addressIndex?: number;
	allowHighFees?: boolean;
}

export interface ManualAutoSendResult {
	txid: string;
	fromAddress: string;
	feeSatoshis: number;
	changeSatoshis: number;
	rawTransactionHex: string;
}

interface WalletScannerUtxo {
	txid: string;
	vout?: number;
	outputIndex?: number;
	satoshis?: number;
	amount?: number;
}

interface WalletScannerUtxoPayload {
	data?: {
		utxos: WalletScannerUtxo[];
		currentBlockHeight?: number;
	};
}

const getDefaultNetwork = (): NetworkSetting => {
	const envNetwork = ((import.meta as any).env?.VITE_ZCASH_NETWORK || 'mainnet').toString().toLowerCase();
	return envNetwork === 'testnet' ? 'testnet' : 'mainnet';
};

const sanitizeIndex = (value: number | undefined, label: string): number => {
	if (value === undefined || value === null || Number.isNaN(value)) {
		return label === 'account' ? DEFAULT_ACCOUNT_INDEX : DEFAULT_ADDRESS_INDEX;
	}

	if (!Number.isInteger(value) || value < 0) {
		throw new Error(`${label} index must be a non-negative integer`);
	}

	if (value > MAX_ALLOWED_INDEX) {
		throw new Error(`${label} index exceeds supported range`);
	}

	return value;
};

const convertAmountToSatoshis = (amountZec: number): number => {
	if (!Number.isFinite(amountZec) || amountZec <= 0) {
		throw new Error('Amount must be greater than zero');
	}

	return Math.round(amountZec * SATOSHIS_PER_ZEC);
};

const formatUtxosForBuilder = (utxos: WalletScannerUtxo[], address: string): MayaUTXO[] => {
	return utxos
		.filter((utxo) => !!utxo.txid)
		.map((utxo) => {
			const satoshis = typeof utxo.satoshis === 'number'
				? utxo.satoshis
				: convertAmountToSatoshis(utxo.amount || 0);

			return {
				address,
				txid: utxo.txid,
				outputIndex: typeof utxo.vout === 'number'
					? utxo.vout
					: (typeof utxo.outputIndex === 'number' ? utxo.outputIndex : 0),
				satoshis
			};
		})
		.filter((utxo) => utxo.satoshis > 0);
};

const fetchTransparentUtxos = async (address: string) => {
	const response = await walletScannerApi.getUtxos(address).catch((error: AxiosError) => {
		const message = (error.response?.data as { error?: string })?.error || error.message;
		throw new Error(`Unable to load UTXOs: ${message}`);
	});

	const payload = (response.data?.data || null) as WalletScannerUtxoPayload['data'] | null;
	return {
		utxos: payload?.utxos || [],
		blockHeight: payload?.currentBlockHeight ?? 0
	};
};

const broadcastRawTransaction = async (rawTxHex: string, allowHighFees: boolean) => {
	const response = await walletScannerApi.broadcastRawTransaction(rawTxHex, allowHighFees).catch((error: AxiosError) => {
		const message = (error.response?.data as { error?: string })?.error || error.message;
		throw new Error(`Broadcast failed: ${message}`);
	});

	const txid = response.data?.data?.txid;
	if (!txid) {
		throw new Error('Broadcast failed: missing txid');
	}

	return txid;
};

export const deriveTransparentKeyFromMnemonic = (
	mnemonic: string,
	options: {
		accountIndex?: number;
		addressIndex?: number;
		network?: NetworkSetting;
	} = {}
) => {
	const trimmedMnemonic = mnemonic.trim().replace(/\s+/g, ' ');
	if (!validateMnemonic(trimmedMnemonic, englishWordlist)) {
		throw new Error('Invalid mnemonic phrase');
	}

	const accountIndex = sanitizeIndex(options.accountIndex, 'account');
	const addressIndex = sanitizeIndex(options.addressIndex, 'address');
	const network = options.network || getDefaultNetwork();

	const seed = mnemonicToSeedSync(trimmedMnemonic, undefined, englishWordlist);
	const root = HDKey.fromMasterSeed(seed);
	const path = `m/44'/${COIN_TYPE}'/${accountIndex}'/0/${addressIndex}`;
	const child = root.derive(path);

	if (!child.privateKey) {
		throw new Error('Unable to derive transparent private key');
	}

	const privateKeyBytes = Buffer.from(child.privateKey);
	const prefix = network === 'mainnet' ? mainnetPrefix : testnetPrefix;
	const address = skToAddr(privateKeyBytes, Buffer.from(prefix));

	return {
		privateKeyHex: privateKeyBytes.toString('hex'),
		address,
		network
	};
};

export const manualAutoSendService = {
	send: async ({
		mnemonic,
		recipient,
		amountZec,
		memo,
		accountIndex,
		addressIndex,
		allowHighFees = false
	}: ManualAutoSendParams): Promise<ManualAutoSendResult> => {
		const { privateKeyHex, address, network } = deriveTransparentKeyFromMnemonic(mnemonic, {
			accountIndex,
			addressIndex
		});

		const { utxos, blockHeight } = await fetchTransparentUtxos(address);
		if (!utxos.length) {
			throw new Error('No spendable UTXOs were found for the derived address');
		}

		const formattedUtxos = formatUtxosForBuilder(utxos, address);
		if (!formattedUtxos.length) {
			throw new Error('UTXO data is incomplete or invalid');
		}

		const amountSatoshis = convertAmountToSatoshis(amountZec);
		const transaction = await buildTx(
			blockHeight,
			address,
			recipient,
			amountSatoshis,
			formattedUtxos,
			network === 'mainnet',
			memo
		);

		const rawTx = await signAndFinalize(
			blockHeight,
			privateKeyHex,
			transaction.inputs,
			transaction.outputs
		);

		const rawTxHex = Buffer.from(rawTx).toString('hex');
		const txid = await broadcastRawTransaction(rawTxHex, allowHighFees);

		const changeOutput = transaction.outputs.find(
			(output) => output.type === 'pkh' && output.address === address
		);

		return {
			txid,
			fromAddress: address,
			feeSatoshis: transaction.fee,
			changeSatoshis: changeOutput?.amount || 0,
			rawTransactionHex: rawTxHex
		};
	}
};

// Export helpers for testing
export const __manualAutoSendInternals = {
	sanitizeIndex,
	convertAmountToSatoshis,
	formatUtxosForBuilder
};

